import type { RumGlobal, DefaultPrivacyLevel } from '@datadog/browser-rum-slim';
import { useCallback, useMemo } from 'react';

interface RumOptions {
  DefaultPrivacyLevel: typeof DefaultPrivacyLevel;
}

type RumOperation = (rum: RumGlobal, options: RumOptions) => void;

/**
 * useDatadogRum
 *
 * Invoke the Datadog Real User Metrics SDK for action logging; lazy loads the SDK on-demand as it is 100K+.
 */
const useDatadogRum = () => {
  const rum = useCallback((op: RumOperation) => {
    // @datadog/browser-rum is a huge dependency, so we lazy load it:
    import('@datadog/browser-rum-slim').then(
      ({ datadogRum, DefaultPrivacyLevel }) =>
        op(datadogRum, { DefaultPrivacyLevel })
    );
  }, []);

  const addError = useCallback(
    (error: Error, context?: Record<string, any>) => {
      rum((rum) => rum.addError(error, context));
    },
    [rum]
  );

  const addAction = useCallback(
    (name: string, context?: Record<string, any>) => {
      rum((rum) => rum.addAction(name, context));
    },
    [rum]
  );

  return useMemo(
    () => ({
      rum,
      addAction,
      addError,
    }),
    [rum, addAction, addError]
  );
};

export default useDatadogRum;
